import { useEffect, useState, type ReactNode } from "react";
import { App as AntApp, Button, Flex, Form, Input, Modal, Space, Typography } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { stepUpWithTtl, stepUpStart } from "../auth.js";
import { cacheStepUpToken } from "../auth/step-up-controller.js";

/**
 * Sensitive-action confirm: STEP-UP (a fresh OTP to the operator's own phone) → run(action) with the
 * short-lived step-up token carrying `scope`. Two phases: (1) collect `fields` + send the OTP,
 * (2) enter the OTP → exchange for the scoped token → run. Ported from the baseline admin.
 */
export function StepUpModal({ open, title, scope, danger, intro, fields, confirmLabel, onClose, onDone, run }: {
  open: boolean; title: ReactNode; scope: string; danger?: boolean; intro?: ReactNode; fields?: ReactNode;
  confirmLabel: string; onClose: () => void; onDone: () => void;
  run: (stepUpToken: string, values: Record<string, unknown>) => Promise<void>;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [phase, setPhase] = useState<"form" | "otp">("form");
  const [vals, setVals] = useState<Record<string, unknown>>({});
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPhase("form"); setVals({}); setCode(""); form.resetFields(); } }, [open, form]);

  const send = async (v: Record<string, unknown>) => {
    setBusy(true);
    try { const r = await stepUpStart(); setVals(v); setPhase("otp"); message.success(`Code sent to your phone …${r.phone_hint ?? ""}`); }
    catch (e) { message.error((e as Error).message); } finally { setBusy(false); }
  };
  const confirm = async () => {
    if (code.length < 6 || busy) return;
    setBusy(true);
    // Cache the elevated token for its full TTL (the IdP's expires_in). A follow-up sensitive action on
    // the same scope inside that window reuses it instead of sending another SMS.
    try { const { token, expiresIn } = await stepUpWithTtl(code, scope); cacheStepUpToken(scope, token, expiresIn); await run(token, vals); onDone(); }
    catch (e) { message.error((e as Error).message); setCode(""); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} destroyOnHidden centered width={400}
      title={<Space><SafetyCertificateOutlined /> {title}</Space>}>
      {phase === "form" ? (
        <Form form={form} layout="vertical" onFinish={send}>
          {intro}
          {fields}
          <Button type="primary" danger={danger} htmlType="submit" block loading={busy}>Send code to my phone</Button>
        </Form>
      ) : (
        <Flex vertical gap={14} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void confirm(); } }}>
          <Typography.Text type="secondary">Sensitive action — enter the 6-digit code we sent to <b>your</b> phone.</Typography.Text>
          <Flex justify="center"><Input.OTP length={6} value={code} onChange={setCode} autoFocus /></Flex>
          <Button type="primary" danger={danger} block loading={busy} disabled={code.length < 6} onClick={() => void confirm()}>{confirmLabel}</Button>
        </Flex>
      )}
    </Modal>
  );
}
